import type { AxiosError } from 'axios';
import type { ApiError, ApiResponse } from '../types/api';
import httpClient from './http/httpClient';

export interface ActivityData {
  [key: string]: unknown;
}

export interface ActivityMetadata {
  reason?: string;
  [key: string]: unknown;
}

export interface ActivityLogEntry {
  id: string;
  userId: string;
  activityType: string;
  data: ActivityData;
  metadata?: ActivityMetadata;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface ActivityFilters {
  userId?: string;
  activityType?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
  // Dashboard/analytics filters
  targetUserRole?: string;
  view?: 'summary' | 'recent' | 'users' | 'logins' | 'dashboard';
  timezone?: string;
  // Optional: allow scoping by client
  clientId?: string;
}

// Response shape from the analytics backend endpoint
interface ActivitySummaryResponse {
  totalActivities: number;
  uniqueUsers: number;
  dailyBreakdown: Record<string, number>;
  activityBreakdown: Record<string, number>;
}

interface LoginAnalyticsResponse {
  totalLogins: number;
  uniqueUsers: number;
  loginTimestamps: string[];
  deviceBreakdown: Record<string, number>;
}

interface TopUserResponse {
  userId: string;
  userEmail: string;
  userRole: string;
  activityCount: number;
  lastActivity: string | null;
}

interface RecentActivityResponse {
  id: string;
  activityType: string;
  userId: string;
  userEmail?: string;
  userRole: string;
  timestamp: string;
  data: Record<string, unknown>;
}

export interface GetActivityDataResponseBody {
  success: boolean;
  view: 'summary' | 'recent' | 'users' | 'logins' | 'dashboard';
  filters: Record<string, unknown>;
  userRole: string;
  scope: 'platform' | 'organization';
  data: {
    summary?: ActivitySummaryResponse;
    recentActivities?: RecentActivityResponse[];
    topUsers?: TopUserResponse[];
    loginAnalytics?: LoginAnalyticsResponse;
  };
}

export const ACTIVITY_TYPES = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  TENDER_CREATED: 'tender_created',
  TENDER_UPDATED: 'tender_updated',
  TENDER_DELETED: 'tender_deleted',
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_DELETED: 'document_deleted',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  DOCUMENT_REGENERATED: 'document_regenerated',
  DOCUMENT_UPDATED: 'document_updated',
  DOCUMENT_REVIEWED: 'document_reviewed',
  AI_SERVICE_USED: 'ai_service_used',
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  ROLE_CHANGED: 'role_changed',
  PASSWORD_RESET: 'password_reset',
  SETTINGS_UPDATED: 'settings_updated',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  HELP_TICKET_CREATED: 'help_ticket_created',
  HELP_TICKET_UPDATED: 'help_ticket_updated',
  CLIENT_CREATED: 'client_created',
  CLIENT_UPDATED: 'client_updated',
  CLIENT_INVITED: 'client_invited',
  USER_INVITED: 'user_invited',
  SECURITY_EVENT: 'security_event',
  SYSTEM_ERROR: 'system_error',
  ERROR_OCCURRED: 'error_occurred',
  DATA_EXPORT: 'data_export',
  DATA_IMPORT: 'data_import',
  PLATFORM_SETTINGS_UPDATED: 'platform_settings_updated',
  TOKEN_GENERATED: 'token_generated',
  SEARCH_PERFORMED: 'search_performed',
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
} as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[keyof typeof ACTIVITY_TYPES];

export const logUserActivity = async (
  activityType: ActivityType,
  data: ActivityData = {},
  metadata: ActivityMetadata = {}
): Promise<ApiResponse<ActivityLogEntry>> => {
  try {
    const response = await httpClient.post<ApiResponse<ActivityLogEntry>>('/logUserActivity', {
      activityType,
      data,
      metadata,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    });
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;
    return {
      success: false,
      error: {
        message: axiosError.message,
        code: axiosError.code || 'UNKNOWN_ERROR',
        details: axiosError.response?.data,
      },
    };
  }
};

export const getActivityData = async (
  filters: ActivityFilters = {}
): Promise<ApiResponse<GetActivityDataResponseBody>> => {
  const queryParams = new URLSearchParams();

  if (filters.userId) {
    queryParams.append('userId', filters.userId);
  }
  if (filters.activityType) {
    queryParams.append('activityType', filters.activityType);
  }
  // Send date-only (YYYY-MM-DD) to match backend aggregation filters
  if (filters.startDate) {
    const startIsoDate = (filters.startDate as Date).toISOString().split('T')[0] as string;
    queryParams.append('startDate', startIsoDate);
  }
  if (filters.endDate) {
    const endIsoDate = (filters.endDate as Date).toISOString().split('T')[0] as string;
    queryParams.append('endDate', endIsoDate);
  }
  if (filters.limit) {
    queryParams.append('limit', filters.limit.toString());
  }
  if (filters.offset) {
    queryParams.append('offset', filters.offset.toString());
  }
  if (filters.targetUserRole) {
    queryParams.append('targetUserRole', filters.targetUserRole);
  }
  if (filters.view) {
    queryParams.append('view', filters.view);
  }
  if (filters.timezone) {
    queryParams.append('timezone', filters.timezone);
  }
  if (filters.clientId) {
    queryParams.append('clientId', filters.clientId);
  }

  // Prevent 304/empty-body issues by cache-busting and disabling caches
  queryParams.append('_', Date.now().toString());
  const raw = await httpClient.get<unknown>(`/getActivityData?${queryParams.toString()}`, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });
  // Always normalize into ApiResponse shape expected by callers
  const body = raw as Partial<GetActivityDataResponseBody> & { success?: boolean };
  return {
    success: body.success !== undefined ? !!body.success : true,
    data: body as GetActivityDataResponseBody,
  };
};

// Convenience functions for common activity types
export const logLogin = (data: ActivityData = {}): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.LOGIN, data);

export const logLogout = (data: ActivityData = {}): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.LOGOUT, data);

export const logTenderCreated = (data: ActivityData = {}): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.TENDER_CREATED, data);

export const logDocumentUploaded = (
  data: ActivityData = {}
): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.DOCUMENT_UPLOADED, data);

export const logAIServiceUsed = (data: ActivityData = {}): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.AI_SERVICE_USED, data);

export const logSecurityEvent = (
  data: ActivityData = {},
  metadata: ActivityMetadata = {}
): Promise<ApiResponse<ActivityLogEntry>> =>
  logUserActivity(ACTIVITY_TYPES.SECURITY_EVENT, data, metadata);
